import React, { useEffect, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { useUserContext } from '../Components/StateContext/UserContext';
import { RouteComponentProps, withRouter, useParams } from 'react-router-dom';
import RestService from '../Services/RestService';

interface TileSpec {
  label: string;
  icon: string;
  href?: string;
  disabled: boolean;
  hidden?: boolean;
  badge?: number;
}

const badgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: '0.5rem',
  right: '0.5rem',
  minWidth: '1.5rem',
  height: '1.5rem',
  padding: '0 0.4rem',
  borderRadius: '0.75rem',
  background: '#2a2859',
  color: '#fff',
  fontSize: '0.8rem',
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};

const renderTile = (tile: TileSpec, key: number) => {
  const card = (
    <div className="ods-status-card frontpage-card" style={{ position: 'relative' }}>
      {tile.badge != null && tile.badge > 0 && (
        <span style={badgeStyle} aria-label={`${tile.badge} meldinger`}>{tile.badge}</span>
      )}
      <div className="ods-status-card__content">
        <span className={`ods-icon ods-icon--size-kilo ods-icon--size-echo-breakpoint-medium ${tile.icon}`} aria-hidden="true"></span>
        <span className="ods-text--size-kilo ods-text--size-echo-breakpoint-medium">{tile.label}</span>
      </div>
    </div>
  );
  return (
    <div key={key} className={tile.disabled ? "ods-grid__column--6 ods-grid__column--4-breakpoint-medium ods-color-text-grayscale-10" : "ods-grid__column--6 ods-grid__column--4-breakpoint-medium"}>
      {tile.disabled || !tile.href ? card : (
        <a href={tile.href} className="o-kom-link" style={{ display: 'block' }}>{card}</a>
      )}
    </div>
  );
};

const FrontpageView: React.FC<RouteComponentProps> = (props) => {
  const { depId, classId, week } = useParams<{ depId: string; classId: string; week: string }>();
  const { department, userRoles } = useUserContext();

  const isParentOnly = userRoles.length > 0 && userRoles.every(r => r === 'parent');
  const isAdmin = userRoles.includes('admin');
  // Local admins get the same reports, but the data is scoped to their own department server-side.
  const isAdminOrLocalAdmin = isAdmin || userRoles.includes('localadmin');

  const [mailCount, setMailCount] = useState<number | undefined>(undefined);

  useEffect(() => {}, [depId, classId, week, department]);

  useEffect(() => {
    if (!isAdmin) return;
    RestService.get<number>('/api/mailbox/Count')
      .then(setMailCount)
      .catch(() => { /* badge is best-effort; ignore errors */ });
  }, [isAdmin]);

  const tiles: TileSpec[] = [
    { label: 'Brukersøk',             icon: 'ods-icon--magnifying-glass-big', href: '/usersearch',                     disabled: !isAdmin },
    { label: 'Min side',              icon: 'ods-icon--user',               href: '/myuserdetails',                  disabled: false },
    { label: 'Rapporter',             icon: 'ods-icon--graph',              href: '/reports',                         disabled: !isAdminOrLocalAdmin },
    { label: 'Konfigurasjon',         icon: 'ods-icon--tool',               href: '/config',                          disabled: !isAdminOrLocalAdmin },
    { label: 'Betalinger',            icon: 'ods-icon--coin-stacks',        href: '/payments',                       disabled: !isAdminOrLocalAdmin, hidden: true }, // TODO: temporarily hidden
    { label: 'Epost',                 icon: 'ods-icon--email',              href: '/fellesepost',                    disabled: !isAdmin, hidden: !isAdmin, badge: mailCount },
    { label: 'Dokumenter',            icon: 'ods-icon--document',           href: '/dokumenter',                     disabled: false },
    { label: 'Bilder',                icon: 'ods-icon--picture',            href: '/bilder',                          disabled: false, hidden: true }, // TODO: temporarily hidden
    { label: 'Påmelding',             icon: 'ods-icon--document',           href: '/pamelding',                      disabled: false, hidden: isParentOnly },
  ];

  const visible = tiles.filter(t => !t.hidden);
  const sorted = [...visible].sort((a, b) => Number(a.disabled) - Number(b.disabled));

  // One continuous grid: tiles flow and wrap by their column span (2-up on mobile, 3-up on desktop).
  // Chunking into fixed rows of 3 left empty half-rows on mobile, so it's a single grid now.
  return (
    <div className="ods-grid ods-grid--gap ods-grid--center ods-margin-top-25 ods-text--align-center">
      {sorted.map((tile, i) => renderTile(tile, i))}
    </div>
  );
};

export default withRouter(FrontpageView);
